/**
 * Routing and the authenticated / unauthenticated split.
 *
 * There is one gate: if the auth context has no user, every route renders the
 * login page. That is simpler and safer than per-route guards, and it means a
 * route added in a later phase is protected by default.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import AppShell from './components/AppShell.jsx';
import { useAuth } from './lib/auth-context.jsx';
import ApiKeysPage from './views/ApiKeysPage.jsx';
import CataloguePage from './views/CataloguePage.jsx';
import ListsPage from './views/ListsPage.jsx';
import LoginPage from './views/LoginPage.jsx';
import LogPage from './views/LogPage.jsx';
import RegistryPage from './views/RegistryPage.jsx';
import RunDetailPage from './views/RunDetailPage.jsx';
import SettingsPage from './views/SettingsPage.jsx';

function NotFoundPage() {
  return (
    <div className="page">
      <div className="empty">
        <h2 className="empty-title">That page does not exist</h2>
        <p className="empty-description">
          The link may be from an older version of the app. Use the sidebar to get back to the
          catalogue.
        </p>
        <div className="empty-actions">
          <a className="btn btn-primary" href="/catalogue">
            Go to the catalogue
          </a>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, isChecking } = useAuth();

  // Hold the frame until the session check answers. Rendering the login page
  // first would flash it at every signed-in user on every hard refresh.
  if (isChecking) {
    return (
      <div className="login-screen">
        <div className="loading-panel">
          <span className="spinner spinner-lg" />
          Loading Leads Scanner
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        {/* The catalogue is the home screen: the app is SIC-first, so the
            entry point is choosing a code, not browsing companies. */}
        <Route path="/" element={<Navigate to="/catalogue" replace />} />
        <Route path="/catalogue" element={<CataloguePage />} />
        <Route path="/registry" element={<RegistryPage />} />
        <Route path="/lists" element={<ListsPage />} />
        <Route path="/log" element={<LogPage />} />
        {/* One run in full: its filters in plain English, its counters, and the
            per-company outcome trail. */}
        <Route path="/log/:uuid" element={<RunDetailPage />} />
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
